// Admin · Pre-order Batch Tracker.
// Operator maintains the public /track-order batch board: batch name, the
// order-date range the batch covers, and a logistics status (dropdown).
// Auth: shared operator password (same token key as community/hero-drop).

import { useCallback, useEffect, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "loadout.admin.community.token";

const STATUSES = [
        { value: "consolidation_china", label: "Consolidation at China warehouse", dot: "#9CA3AF" },
        { value: "in_transit_china",    label: "In transit from China",            dot: "#3B82F6" },
        { value: "customs_awaiting",    label: "At Customs — awaiting clearance",  dot: "#F59E0B" },
        { value: "customs_cleared",     label: "Customs Cleared",                  dot: "#22D3EE" },
        { value: "received_noida",      label: "Received at Noida warehouse — dispatching in 1–2 working days", dot: "#22C55E" },
];

function useToken() {
        const [token, setToken] = useState(() => {
                try { return window.localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
        });
        const save = useCallback((t) => {
                setToken(t);
                try { window.localStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ }
        }, []);
        const clear = useCallback(() => {
                setToken("");
                try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
        }, []);
        return [token, save, clear];
}

function LoginPanel({ onLogin }) {
        const [pw, setPw] = useState("");
        const [busy, setBusy] = useState(false);
        const [err, setErr] = useState("");
        const submit = async (e) => {
                e.preventDefault();
                setErr(""); setBusy(true);
                try {
                        const r = await fetch(`${BACKEND}/api/admin/community/login`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ password: pw }),
                        });
                        if (!r.ok) { setErr(r.status === 401 ? "Wrong password." : `Login failed (${r.status}).`); return; }
                        const j = await r.json();
                        onLogin(j.token);
                } catch { setErr("Network error. Try again."); } finally { setBusy(false); }
        };
        return (
                <main data-testid="admin-batches-login-page" className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
                        <form onSubmit={submit} className="w-full max-w-[420px] border border-white/15 bg-black">
                                <div className="border-b border-white/10 px-6 py-6">
                                        <p className="font-mono text-[11px] tracking-[0.18em] text-[#FF9F1C]">LOADOUT / ADMIN</p>
                                        <h1 className="mt-2 font-display text-[26px] uppercase">Pre-order Batches</h1>
                                        <p className="mt-3 font-sans text-[13px] text-white/60">
                                                Manage the batch board shown on /track-order. Same operator password as the other admin pages.
                                        </p>
                                </div>
                                <div className="px-6 py-6 space-y-4">
                                        <input
                                                data-testid="admin-batches-login-password"
                                                type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                                                placeholder="Operator password" autoFocus
                                                className="w-full bg-transparent border border-white/20 px-3 py-3 font-mono text-[13px] placeholder:text-white/30 focus:outline-none focus:border-[#FF9F1C]"
                                        />
                                        {err && <p data-testid="admin-batches-login-error" className="font-mono text-[11px] text-red-400">{err}</p>}
                                        <button
                                                data-testid="admin-batches-login-submit"
                                                type="submit" disabled={busy}
                                                className="w-full border border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] font-mono text-[12px] tracking-[0.2em] uppercase py-3.5 disabled:opacity-50"
                                        >
                                                {busy ? "Signing in…" : "Sign in"}
                                        </button>
                                </div>
                        </form>
                </main>
        );
}

function StatusSelect({ value, onChange, testid }) {
        return (
                <select
                        data-testid={testid}
                        value={value} onChange={(e) => onChange(e.target.value)}
                        className="w-full bg-black border border-white/20 px-3 py-2.5 font-mono text-[13px] text-white focus:outline-none focus:border-[#FF9F1C]"
                >
                        {STATUSES.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                </select>
        );
}

function StatusBadge({ value }) {
        const s = STATUSES.find((x) => x.value === value) || { label: value, dot: "#6B7280" };
        return (
                <span className="inline-flex items-start gap-2 text-[13px] text-white/85">
                        <span
                                aria-hidden="true"
                                className="inline-block w-2.5 h-2.5 rounded-full mt-1 shrink-0"
                                style={{ backgroundColor: s.dot, boxShadow: `0 0 8px ${s.dot}66` }}
                        />
                        {s.label}
                </span>
        );
}

function CreateBatchForm({ token, onCreated }) {
        const [name, setName] = useState("");
        const [from, setFrom] = useState("");
        const [to, setTo] = useState("");
        const [status, setStatus] = useState(STATUSES[0].value);
        const [busy, setBusy] = useState(false);
        const [err, setErr] = useState("");

        const submit = async (e) => {
                e.preventDefault();
                if (!name.trim()) { setErr("Batch name required."); return; }
                setErr(""); setBusy(true);
                try {
                        const r = await fetch(`${BACKEND}/api/admin/preorder-batches`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({
                                        batch_name: name.trim(), orders_from: from || null,
                                        orders_to: to || null, status,
                                }),
                        });
                        const j = await r.json().catch(() => ({}));
                        if (!r.ok) { setErr(j.detail || `Create failed (${r.status}).`); return; }
                        setName(""); setFrom(""); setTo(""); setStatus(STATUSES[0].value);
                        onCreated();
                } catch { setErr("Network error."); } finally { setBusy(false); }
        };

        return (
                <form onSubmit={submit} className="border border-white/15 bg-black p-6 space-y-5" data-testid="admin-batch-create-form">
                        <div>
                                <label className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/60">Batch name</label>
                                <input
                                        data-testid="admin-batch-name"
                                        value={name} onChange={(e) => setName(e.target.value)}
                                        placeholder="Batch 07 — Keyboards Drop"
                                        className="mt-2 w-full bg-transparent border border-white/20 px-3 py-2.5 font-mono text-[13px] text-white placeholder:text-white/25 focus:outline-none focus:border-[#FF9F1C]"
                                />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                        <label className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/60">Orders from</label>
                                        <input
                                                data-testid="admin-batch-from"
                                                type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                                                className="mt-2 w-full bg-transparent border border-white/20 px-3 py-2.5 font-mono text-[13px] text-white focus:outline-none focus:border-[#FF9F1C] [color-scheme:dark]"
                                        />
                                </div>
                                <div>
                                        <label className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/60">Orders to</label>
                                        <input
                                                data-testid="admin-batch-to"
                                                type="date" value={to} onChange={(e) => setTo(e.target.value)}
                                                className="mt-2 w-full bg-transparent border border-white/20 px-3 py-2.5 font-mono text-[13px] text-white focus:outline-none focus:border-[#FF9F1C] [color-scheme:dark]"
                                        />
                                </div>
                        </div>
                        <div>
                                <label className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/60">Current status</label>
                                <div className="mt-2"><StatusSelect value={status} onChange={setStatus} testid="admin-batch-status" /></div>
                        </div>
                        {err && <p data-testid="admin-batch-create-error" className="font-mono text-[11px] text-red-400">{err}</p>}
                        <button
                                data-testid="admin-batch-create-submit"
                                type="submit" disabled={busy}
                                className="border border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] font-mono text-[12px] tracking-[0.2em] uppercase px-6 py-3 disabled:opacity-50"
                        >
                                {busy ? "Saving…" : "Add batch"}
                        </button>
                </form>
        );
}

function BatchRow({ batch, token, onChanged }) {
        const [busy, setBusy] = useState(false);
        const [editingStatus, setEditingStatus] = useState(batch.status);
        const dirty = editingStatus !== batch.status;

        const saveStatus = async () => {
                setBusy(true);
                try {
                        const r = await fetch(`${BACKEND}/api/admin/preorder-batches/${batch.id}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ status: editingStatus }),
                        });
                        const j = await r.json().catch(() => ({}));
                        if (r.ok) {
                                if (editingStatus === "received_noida") {
                                        window.alert(j.notifying
                                                ? `Emailing ${j.notifying} pre-order customer${j.notifying === 1 ? "" : "s"} that their order has landed.`
                                                : "Saved. No pre-order customers on record for this batch's dates, so no emails were sent.");
                                }
                                onChanged();
                        }
                } finally { setBusy(false); }
        };

        const remove = async () => {
                if (!window.confirm(`Delete batch "${batch.batch_name}"?`)) return;
                setBusy(true);
                try {
                        const r = await fetch(`${BACKEND}/api/admin/preorder-batches/${batch.id}`, {
                                method: "DELETE", headers: { Authorization: `Bearer ${token}` },
                        });
                        if (r.ok) onChanged();
                } finally { setBusy(false); }
        };

        const fmt = (iso) => {
                if (!iso) return "—";
                try { return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
                catch { return iso; }
        };

        return (
                <div className="border border-white/10 bg-black p-4 flex flex-col gap-3" data-testid="admin-batch-row">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                        <p className="font-display text-[20px] uppercase text-white">{batch.batch_name}</p>
                                        <p className="mt-1 font-mono text-[11px] text-white/50">
                                                Orders {fmt(batch.orders_from)} → {fmt(batch.orders_to)}
                                        </p>
                                </div>
                                <StatusBadge value={batch.status} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-end">
                                <StatusSelect value={editingStatus} onChange={setEditingStatus} testid={`admin-batch-status-${batch.id}`} />
                                <button
                                        data-testid={`admin-batch-save-${batch.id}`}
                                        onClick={saveStatus} disabled={busy || !dirty}
                                        className="border border-[#FF9F1C] text-[#FF9F1C] font-mono text-[11px] tracking-[0.18em] uppercase px-4 py-2.5 hover:bg-[#FF9F1C] hover:text-[#0A0A0A] disabled:opacity-40"
                                >
                                        Save status
                                </button>
                                <button
                                        data-testid={`admin-batch-delete-${batch.id}`}
                                        onClick={remove} disabled={busy}
                                        className="border border-red-500/40 text-red-400 font-mono text-[11px] tracking-[0.18em] uppercase px-4 py-2.5 hover:bg-red-500 hover:text-black disabled:opacity-40"
                                >
                                        Delete
                                </button>
                        </div>
                </div>
        );
}

export default function AdminPreorderBatches() {
        const [token, saveToken, clearToken] = useToken();
        const [batches, setBatches] = useState([]);
        const [loading, setLoading] = useState(false);

        const load = useCallback(async () => {
                if (!token) return;
                setLoading(true);
                try {
                        const r = await fetch(`${BACKEND}/api/admin/preorder-batches/list`, {
                                headers: { Authorization: `Bearer ${token}` },
                        });
                        if (r.status === 401) { clearToken(); return; }
                        const j = await r.json();
                        setBatches(j.batches || []);
                } finally { setLoading(false); }
        }, [token, clearToken]);

        useEffect(() => { load(); }, [load]);

        if (!token) return <LoginPanel onLogin={saveToken} />;

        return (
                <main data-testid="admin-batches-page" className="min-h-screen bg-[#0A0A0A] text-white px-6 py-12">
                        <div className="mx-auto max-w-[900px]">
                                <div className="flex items-center justify-between flex-wrap gap-4">
                                        <div>
                                                <p className="font-mono text-[11px] tracking-[0.18em] text-[#FF9F1C]">LOADOUT / ADMIN</p>
                                                <h1 className="mt-2 font-display text-[32px] uppercase">Pre-order Batches</h1>
                                                <p className="mt-2 font-sans text-[13px] text-white/60 max-w-[64ch]">
                                                        Everything here is public on /track-order the moment you save. Update the status dropdown as each batch moves.
                                                </p>
                                        </div>
                                        <button data-testid="admin-batches-signout" onClick={clearToken} className="font-mono text-[11px] tracking-[0.18em] uppercase border border-white/25 px-4 py-2 hover:border-[#FF9F1C] hover:text-[#FF9F1C]">
                                                Sign out
                                        </button>
                                </div>

                                <section className="mt-10">
                                        <CreateBatchForm token={token} onCreated={load} />
                                </section>

                                <section className="mt-12">
                                        <h2 className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/60">Live batches ({batches.length})</h2>
                                        <div className="mt-4 space-y-3" data-testid="admin-batches-list">
                                                {loading && <p className="font-mono text-[11px] text-white/40">Loading…</p>}
                                                {!loading && batches.length === 0 && (
                                                        <p className="font-mono text-[11px] text-white/40">No batches yet. Add one above.</p>
                                                )}
                                                {batches.map((b) => (
                                                        <BatchRow key={b.id} batch={b} token={token} onChanged={load} />
                                                ))}
                                        </div>
                                </section>
                        </div>
                </main>
        );
}
